import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, notificationService } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          // fetchUser might return a 403, but we handle it inside fetchUser to set the user state
          await fetchUser();
          
          // Only fetch notifications if confirmed or admin
          // (We'll check user state after fetchUser)
        } catch (error) {
          console.error('Initial fetch failed:', error);
          if (error.response && error.response.status === 401) {
            logout();
          }
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  // Separate effect for notifications to handle the user state correctly
  useEffect(() => {
    if (token && user && (user.is_confirmed || user.is_admin)) {
      fetchNotifications();
      // Fallback-Polling, falls SSE ausfaellt. Echtzeit laeuft ueber useSSE;
      // dies ist nur ein Sicherheitsnetz, daher moderat (45s statt 2min).
      const interval = setInterval(fetchNotifications, 45000);
      return () => clearInterval(interval);
    }
  }, [token, user]);

  const fetchUser = async () => {
    try {
      const response = await authService.getMe();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // If we get a 403 with needs_confirmation or needs_verification, 
      // we still want to set the user state if the backend provided user data
      if (error.response && error.response.status === 403 && error.response.data.user) {
        setUser(error.response.data.user);
      }
    }
  };

  const fetchNotifications = async () => {
    // Only attempt to fetch if the user is confirmed or admin
    if (!user || (!user.is_confirmed && !user.is_admin)) {
      return;
    }
    try {
      const response = await notificationService.getAll();
      if (response.data && response.data.notifications) {
        setNotifications(response.data.notifications);
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markNotificationRead = async (notifId) => {
    // Only adjust the badge if this notification was actually unread, so
    // re-reading an already-read item can't drift the counter.
    const wasUnread = notifications.some(n => n.id === notifId && !n.read);
    try {
      await notificationService.markAsRead(notifId);
      setNotifications(prev =>
        prev.map(n => n.id === notifId ? { ...n, read: true } : n)
      );
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications read:', error);
    }
  };

  const login = async (email, password) => {
    const response = await authService.login(email, password);
    const { token: newToken, user: userData } = response.data;
    
    // Set token in state first to ensure hooks update
    setToken(newToken);
    localStorage.setItem('token', newToken);
    setUser(userData);
    
    // Request notification permission on login
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    return userData;
  };

  const register = async (email, username, password, paypalLink) => {
    const response = await authService.register(email, username, password, paypalLink);
    const { token: newToken, user: userData, verification_code } = response.data;
    
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);

    // Request notification permission on register
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    return { user: userData, verification_code };
  };

  const verifyEmail = async (code) => {
    const response = await authService.verifyEmail(code);
    setUser(prev => ({ ...prev, email_verified: true }));
    return response.data;
  };

  const resendVerification = async () => {
    const response = await authService.resendVerification();
    return response.data;
  };

  const requestPasswordReset = async (email) => {
    const response = await authService.requestPasswordReset(email);
    return response.data;
  };

  const resetPassword = async (email, code, newPassword) => {
    const response = await authService.resetPassword(email, code, newPassword);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setNotifications([]);
    setUnreadCount(0);
    // Force immediate navigation to login
    window.location.href = '/login';
  };

  const refreshUser = async () => {
    if (token) {
      await fetchUser();
    }
  };

  const updateProfile = async (data) => {
    const response = await authService.updateProfile(data);
    await fetchUser();
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout, 
      refreshUser,
      updateProfile,
      verifyEmail,
      resendVerification,
      requestPasswordReset,
      resetPassword,
      notifications,
      unreadCount,
      markNotificationRead,
      markAllNotificationsRead,
      fetchNotifications
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
